import { useCallback, useState } from 'react'
import { ClipboardCheck } from 'lucide-react'
import { Shell, useShellMeta } from '../../../admin/Shell.js'
import { Tabs, usePermissions } from '../../../admin/primitives.js'
import { Link } from '../../../router.js'
import { fmtHeure } from '../../api.js'
import { fmtNombre, useVueSonde } from '../commun.js'
import { OngletAValider } from './AValider.js'
import { OngletAppareils } from './Appareils.js'
import { OngletImport } from './ImportTechlc.js'
import { OngletPropositions } from './Propositions.js'
import { SORTES_REGISTRE, estListeAppareils, estListeDecouvertes, estListePropositions, estListeSalles } from './modele.js'
import './registre.css'

/**
 * SUPERVISION › REGISTRE ET « À VALIDER » (21/10/2026) — `/infrastructure/registre`.
 *
 * Le registre de Sonde : les POSTES surveillés (un rôle à un emplacement, uid
 * immuable) et leurs identifiants. Tant qu'un appareil découvert n'est pas
 * validé, il n'existe pas pour la supervision : aucune salle ne l'affiche,
 * personne n'est prévenu s'il tombe. Quatre onglets : À valider, Appareils,
 * Import TECH_LC, Propositions.
 *
 * Droits : sonde.details.read pour voir (identifiants, MAC, preuves),
 * sonde.registry.manage pour agir — sans lui, les gestes sont ABSENTS et un
 * bandeau discret dit « Lecture seule ». Tout passe par le relais de Gestion.
 *
 * Adresse : `?onglet=a-valider|appareils|import|propositions` et
 * `&appareil=<uid>` ouvre directement la fiche d'édition d'un poste.
 */

type Onglet = 'a-valider' | 'appareils' | 'import' | 'propositions'
const ONGLETS: readonly Onglet[] = ['a-valider', 'appareils', 'import', 'propositions']

function lireAdresse(): { onglet: Onglet; appareil: string | null } {
  try {
    const p = new URLSearchParams(window.location.search)
    const o = p.get('onglet') as Onglet | null
    const a = p.get('appareil')
    return { onglet: a ? 'appareils' : o && ONGLETS.includes(o) ? o : 'a-valider', appareil: a && /^[a-z0-9][a-z0-9-]{1,62}$/.test(a) ? a : null }
  } catch { return { onglet: 'a-valider', appareil: null } }
}

function ecrireAdresse(onglet: Onglet, appareil: string | null) {
  try {
    const p = new URLSearchParams(window.location.search)
    p.set('onglet', onglet)
    if (appareil) p.set('appareil', appareil)
    else p.delete('appareil')
    window.history.replaceState(window.history.state, '', `${window.location.pathname}?${p.toString()}`)
  } catch { /* adresse non modifiable : l'écran fonctionne quand même */ }
}

export function RegistrePage() {
  const meta = useShellMeta()
  const { permissions, loading } = usePermissions()
  const peutVoir = permissions.includes('sonde.details.read')
  const peutGerer = permissions.includes('sonde.registry.manage')
  const initial = useState(lireAdresse)[0]
  const [onglet, setOnglet] = useState<Onglet>(initial.onglet)
  const [appareil, setAppareil] = useState<string | null>(initial.appareil)

  const actif = !loading && peutVoir
  const decouvertes = useVueSonde(actif ? 'discovery?status=pending&limit=1' : null, estListeDecouvertes, 60_000)
  const appareils = useVueSonde(actif ? 'devices?limit=1' : null, estListeAppareils, 60_000)
  const propositions = useVueSonde(actif ? `proposals?status=proposed&kind=${SORTES_REGISTRE.join(',')}&limit=200` : null, estListePropositions, 120_000)
  const salles = useVueSonde(actif ? 'registry/zones' : null, estListeSalles, 300_000)

  const rDec = decouvertes.recharger, rApp = appareils.recharger, rProp = propositions.recharger
  const recompter = useCallback(() => { rDec(); rApp(); rProp() }, [rDec, rApp, rProp])

  const nDec = decouvertes.lecture?.etat === 'ok' ? decouvertes.lecture.data.counts : decouvertes.precedente?.counts ?? null
  const nApp = appareils.lecture?.etat === 'ok' ? appareils.lecture.data.total : appareils.precedente?.total ?? null
  const lp = propositions.lecture?.etat === 'ok' ? propositions.lecture.data : propositions.precedente
  const nProp = lp ? lp.items.length : null
  const listeSalles = salles.lecture?.etat === 'ok' ? salles.lecture.data.zones : salles.precedente?.zones ?? null
  const luApp = appareils.lecture?.etat === 'ok' ? appareils.lecture.data.generated_at : null

  const changerOnglet = (o: string) => {
    const k = (ONGLETS as readonly string[]).includes(o) ? o as Onglet : 'a-valider'
    setOnglet(k); setAppareil(null); ecrireAdresse(k, null)
  }
  const ouvrirAppareil = (uid: string | null) => { setAppareil(uid); ecrireAdresse('appareils', uid) }

  const compte = (n: number | null | undefined, plus = false) => (n === null || n === undefined ? '' : ` (${fmtNombre(n)}${plus ? '+' : ''})`)

  return (
    <Shell active="infrastructure" meta={meta}>
      <main className="lc-page lc-page--wide lc-stagger sv-rgs-page">
        <Link className="lc-crumb" href="/infrastructure">← Supervision</Link>
        <h1 className="lc-title sv-rgs-h1"><ClipboardCheck size={22} aria-hidden="true" /> Registre et « À valider »</h1>
        <p className="lc-subtitle">
          Les appareils que Sonde surveille. Un appareil découvert n’apparaît dans sa salle, et n’alerte, qu’une fois validé ici.
        </p>

        {!loading && !peutVoir ? (
          <p className="sv-rgs-lecture-seule sv-section" role="status">Votre compte ne permet pas de voir le détail du registre de la supervision.</p>
        ) : null}

        {actif ? (
          <>
            <div className="sv-rgs-tuiles sv-section">
              <button type="button" className="sv-rgs-tuile sv-rgs-tuile--attente" onClick={() => changerOnglet('a-valider')}>
                <span className="sv-rgs-tuile__chiffre">{fmtNombre(nDec?.pending)}</span>
                <span className="sv-rgs-tuile__titre">découverte{(nDec?.pending ?? 0) > 1 ? 's' : ''} à valider</span>
                <span className="sv-rgs-tuile__meta">{nDec ? `${fmtNombre(nDec.accepted)} validées · ${fmtNombre(nDec.ignored)} ignorées` : '—'}</span>
              </button>
              <button type="button" className="sv-rgs-tuile" onClick={() => changerOnglet('appareils')}>
                <span className="sv-rgs-tuile__chiffre">{fmtNombre(nApp)}</span>
                <span className="sv-rgs-tuile__titre">poste{(nApp ?? 0) > 1 ? 's' : ''} actif{(nApp ?? 0) > 1 ? 's' : ''} au registre</span>
                <span className="sv-rgs-tuile__meta">{luApp ? `état à ${fmtHeure(luApp)}` : '—'}</span>
              </button>
              <button type="button" className="sv-rgs-tuile" onClick={() => changerOnglet('propositions')}>
                <span className="sv-rgs-tuile__chiffre">{fmtNombre(nProp)}{lp?.next_cursor ? '+' : ''}</span>
                <span className="sv-rgs-tuile__titre">proposition{(nProp ?? 0) > 1 ? 's' : ''} à décider</span>
                <span className="sv-rgs-tuile__meta">domaines d’alimentation, fiches fantômes, remplacements</span>
              </button>
            </div>

            <div className="sv-rgs-onglets sv-section">
              <Tabs active={onglet} onChange={changerOnglet} tabs={[
                { key: 'a-valider', label: `À valider${compte(nDec?.pending)}` },
                { key: 'appareils', label: `Appareils${compte(nApp)}` },
                { key: 'import', label: 'Import TECH_LC' },
                { key: 'propositions', label: `Propositions${compte(nProp, !!lp?.next_cursor)}` },
              ]} />
            </div>

            {onglet === 'a-valider' ? <OngletAValider peutGerer={peutGerer} salles={listeSalles} onCompte={recompter} /> : null}
            {onglet === 'appareils' ? (
              <OngletAppareils peutGerer={peutGerer} salles={listeSalles} uidOuvert={appareil} onOuvrir={ouvrirAppareil} onCompte={recompter} />
            ) : null}
            {onglet === 'import' ? <OngletImport peutGerer={peutGerer} onCompte={recompter} /> : null}
            {onglet === 'propositions' ? <OngletPropositions peutGerer={peutGerer} onCompte={recompter} /> : null}
          </>
        ) : null}
      </main>
    </Shell>
  )
}
